import { invoke } from '@tauri-apps/api/core';
import { isDesktop } from './runtime';

/**
 * Zold keret a megosztott KIJELZO szelen.
 *
 * Harom kijelzo mellett megosztas kozben nem latszik, MELYIKET latjak a
 * tobbiek. A hivas ablakaban levo keret erre nem valasz: a megosztott monitor
 * eppen a masik.
 *
 * A rajzolast az asztali app vegzi (`show_share_frame`), mert egy weblap a
 * sajat ablakan kivulre nem rajzolhat. Bongeszoben ezert nincs keret - ezt a
 * felulet ki is mondja, hogy ne tunjon elrontottnak.
 */

/** Mi tortent - a felulet ebbol tudja, kell-e kerdeznie vagy magyarazkodnia. */
export type ShareFrameState =
  /** Nem osztunk meg kepernyot. */
  | 'off'
  /** Megjelent a keret a kijelzo szelen. */
  | 'shown'
  /** Bongeszoben vagyunk: a kijelzore nem tudunk rajzolni. */
  | 'browser'
  /** Nem egesz kijelzot osztunk meg, hanem egy ablakot vagy egy bongeszofulet. */
  | 'not-a-screen'
  /** Nem tudtuk eldonteni, melyik kijelzo - a felhasznalonak kell valasztania. */
  | 'ask';

export interface Monitor {
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  primary: boolean;
}

/**
 * A legutobb valasztott kijelzo.
 *
 * **Ez NEM dont, csak segit.** A valasztot ez emeli ki, hogy a szokasos
 * monitor egy kattintas legyen - de sosem alkalmazzuk magatol.
 *
 * Elsore ugyanis alkalmaztuk, es rossz lett: a megosztott kijelzo
 * MEGOSZTASONKENT valtozhat. Aki tegnap a jobb szelsot mutatta, ma a kozepsot
 * osztja meg - es a keret a tegnapi monitorra kerult. Egy rossz helyre tett
 * keret pedig rosszabb a semminel: hamis biztonsagot ad arrol, mit lat a
 * masik fel.
 */
const STORAGE_KEY = 'avslack.huddle.share-monitor';

export function rememberedMonitor(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function rememberMonitor(name: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, name);
  } catch {
    // A tarolas hianya nem torheti el a megosztast.
  }
}

/**
 * A rogzitett forras FIZIKAI merete.
 *
 * Ebbol talalja meg az asztali app, melyik kijelzorol van szo - ha egyertelmu.
 *
 * **A `getCapabilities` az elsodleges forras**, nem a `getSettings`: a
 * media-konyvtar a megosztast atmeretezheti (jellemzoen 1080p-re), es akkor a
 * beallitasokban mar a KICSINYITETT meret all, ami egyetlen monitorra sem
 * illik. A kepesseg viszont a forras sajat felbontasat mondja meg.
 */
export function sourceSize(track: MediaStreamTrack): { width: number; height: number } | null {
  const settings = track.getSettings() as MediaTrackSettings & { displaySurface?: string };

  // Ablak- vagy fulmegosztasnal nincs mit bekeretezni: az nem egy kijelzo.
  if (settings.displaySurface !== 'monitor') return null;

  const caps = track.getCapabilities?.() as
    { width?: { max?: number }; height?: { max?: number } } | undefined;

  const width = caps?.width?.max ?? settings.width;
  const height = caps?.height?.max ?? settings.height;

  return width && height ? { width, height } : null;
}

export async function showShareFrame(track: MediaStreamTrack): Promise<ShareFrameState> {
  const size = sourceSize(track);
  if (!size) return 'not-a-screen';
  if (!isDesktop()) return 'browser';

  /*
    A megjegyzett valasztast SZANDEKOSAN nem adjuk at.

    Csak akkor rajzolunk magunktol, ha a meret EGYERTELMUEN dont; kulonben
    kerdezunk. Lasd `rememberedMonitor` - ott all, miert.
  */
  const chosen = await invoke<string | null>('show_share_frame', size).catch(() => null);

  return chosen ? 'shown' : 'ask';
}

/** A felhasznalo valasztott: rajzoljuk oda, es jegyezzuk is meg. */
export async function pickShareMonitor(
  track: MediaStreamTrack,
  name: string,
): Promise<ShareFrameState> {
  const size = sourceSize(track);
  if (!size || !isDesktop()) return 'browser';

  const chosen = await invoke<string | null>('show_share_frame', { ...size, monitor: name }).catch(
    () => null,
  );

  if (!chosen) return 'ask';
  rememberMonitor(chosen);
  return 'shown';
}

export async function listMonitors(): Promise<Monitor[]> {
  if (!isDesktop()) return [];
  return invoke<Monitor[]>('list_monitors').catch(() => []);
}

export async function hideShareFrame(): Promise<void> {
  if (!isDesktop()) return;
  await invoke('hide_share_frame').catch(() => undefined);
}

// ─── Kijelzo-felismeres felvillanassal ──────────────────────────────────────

/**
 * A pottyok szinei - INDEX SZERINT, ugyanabban a sorrendben, mint a Rust
 * oldalon (`PROBE_COLORS`). Csupa telitett alapszin, egymastol tavol: a
 * megosztott kep tomoritve erkezik vissza, ezert ket hasonlo arnyalat
 * osszemosodna.
 */
const PROBE_COLORS: [number, number, number][] = [
  [255, 0, 0],
  [0, 255, 0],
  [0, 0, 255],
  [255, 255, 0],
  [255, 0, 255],
  [0, 255, 255],
];

/** A potty merete keppontban - egyeznie kell a Rust oldali `PROBE_SIZE`-zal. */
const PROBE_SIZE = 20;

/**
 * Ennyit varunk, hogy a potty biztosan bekeruljon a megosztott kepbe.
 *
 * Nemcsak a kepkockara var: a potty lapjanak is be kell toltenie. Az elso
 * hasznalat utan a gyorsitotarbol jon, de az elso alkalommal halozati kerest
 * jelent.
 */
const PROBE_SETTLE_MS = 700;

/**
 * Egy kep a megosztott savbol, es a BAL FELSO sarok atlagszine.
 *
 * A megosztott kep bal felso sarka a kijelzo bal felso sarka - eppen oda
 * tesszuk a pottyot. A szelet nem mintavetelezzuk: a tomorites ott mos a
 * legtobbet.
 */
async function topLeftColor(
  track: MediaStreamTrack,
  monitorWidth: number,
): Promise<[number, number, number] | null> {
  const video = document.createElement('video');
  video.srcObject = new MediaStream([track]);
  video.muted = true;
  video.playsInline = true;

  try {
    await video.play();
    // Egy kesz kepkockat varunk, nem csak a lejatszas indulasat.
    await new Promise<void>((resolve) => {
      if (video.readyState >= 2) resolve();
      else video.addEventListener('loadeddata', () => resolve(), { once: true });
    });

    const width = video.videoWidth;
    if (!width) return null;

    /*
      A megosztast a media-konyvtar atmeretezheti, ezert a potty a kepen mar
      nem 20 keppont. A kicsinyites aranyabol szamoljuk, hova essen a minta.
    */
    const scale = width / monitorWidth;
    const box = Math.max(4, Math.round(PROBE_SIZE * scale));
    const inset = Math.max(1, Math.round(box * 0.25));
    const side = Math.max(2, box - inset * 2);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;

    ctx.drawImage(video, 0, 0);
    const { data } = ctx.getImageData(inset, inset, side, side);

    let r = 0;
    let g = 0;
    let b = 0;
    const pixels = data.length / 4;
    for (let i = 0; i < data.length; i += 4) {
      r += data[i] ?? 0;
      g += data[i + 1] ?? 0;
      b += data[i + 2] ?? 0;
    }
    return [r / pixels, g / pixels, b / pixels];
  } catch {
    return null;
  } finally {
    video.pause();
    video.srcObject = null;
  }
}

/**
 * MELYIK kijelzot osztjuk meg? - megkerdezzuk magat a kepet.
 *
 * Minden szoba joheto monitor bal felso sarkaba teszunk egy apro, mas-mas
 * szinu pottyot, majd a megosztott kepbol kiolvassuk, melyik szin latszik.
 * Ez nem talalgatas, hanem meres - es ezert lehet kerdes nelkul is helyes.
 *
 * **Csak BIZTOS talalatot fogadunk el**: a legjobb egyezesnek kozel kell
 * lennie, es a masodiknak erezhetoen tavolabb. Bizonytalan eredmenynel `null`
 * - olyankor a felulet megkerdezi a felhasznalot, ahogy eddig. A rossz
 * kijelzore tett keret rosszabb a semminel.
 */
export async function probeMonitor(
  track: MediaStreamTrack,
  candidates: Monitor[],
): Promise<string | null> {
  if (!isDesktop() || candidates.length === 0) return null;

  const names = candidates.slice(0, PROBE_COLORS.length).map((m) => m.name);
  const ok = await invoke('show_probe_dots', { monitors: names })
    .then(() => true)
    .catch(() => false);
  if (!ok) return null;

  try {
    await new Promise((resolve) => setTimeout(resolve, PROBE_SETTLE_MS));

    const seen = await topLeftColor(track, candidates[0]?.width ?? 1920);
    if (!seen) return null;

    const scored = names
      .map((name, index) => {
        const [r, g, b] = PROBE_COLORS[index] ?? [0, 0, 0];
        const distance = Math.hypot(seen[0] - r, seen[1] - g, seen[2] - b);
        return { name, distance };
      })
      .sort((a, b) => a.distance - b.distance);

    const best = scored[0];
    const second = scored[1];
    if (!best) return null;

    /*
      A ket kuszob egyutt dont:
       - a talalatnak KOZEL kell lennie (a tomorites tompit, de nem forgat);
       - es erezhetoen kozelebb, mint a masodik (kulonben ket szin kozott
         allunk, es akkor inkabb kerdezunk).
    */
    const clearlyClosest = !second || second.distance > best.distance * 1.6;
    return best.distance < 140 && clearlyClosest ? best.name : null;
  } finally {
    await invoke('hide_probe_dots').catch(() => undefined);
  }
}
